import React, { ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CollapsibleSection,
  CollapsibleSectionContext,
  Flex,
  IconButton,
  InlineLink,
  InputDescription,
  Pane,
  Text,
  TextArea,
} from '@fullstory/components';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { useFlags } from '@fullstory/react-hooks';
import css from './index.module.scss';
import { containsNonDescendantCombinator, SelectorRule } from '@fullstory/selectors';
import { FormattedMessage, useIntl } from 'react-intl';
import { MultiPlatformSelection } from '../../MultiPlatformSelection';
import { ValidateNamedElementResponse } from '@fullstory/proto/fs/services/dx-objects';
import { useElementPickerSignalStudio } from '../../../hooks';
import { AppearancesContext, InSignalStudioContext } from '@fullstory/signal-studio-context';
import { useExperimentConfig } from '@fullstory/library-ui';
import { ExperimentFeature } from '@fullstory/proto/fs/services/orgs';
import classNames from 'classnames';
import { SelectorTree } from '../../SelectorTree';
import { useSelectorTree } from '../../../hooks/useSelectorTree';
import { SelectorTrimButton } from '../../SelectorTrimButton';
import { PlatformSelectorConfig } from '../../../types';
import { SelectorFixButton } from '../../SelectorFixButton';
import { useOptimizeUtil } from '../../../hooks/useOptimizeUtil';
import { Event } from '@fullstory/proto/fs/api/segment';
import { isSupportedSelectorForSearchAndAnalytics, makePlatformSelectorDeps } from '../../../utils';
import { Metric } from '@fullstory/proto/fs/services/search-settings';
import { SelectorTrimmingBanner, SelectorTrimmingState } from '../../SelectorTrimmingBanner';
import { WarningBanner } from '@fullstory/dqm-ui';

interface SelectorInputTreeProps {
  innerRef?: React.Ref<HTMLTextAreaElement>;
  selector: PlatformSelectorConfig;
  validationState: ValidateNamedElementResponse.ValidationState | undefined;
  onChange: (selectors: PlatformSelectorConfig) => void;
  onDelete?: () => void;
  onFocus?: () => void;
  onHoverSelector: (selector?: string) => void;
  disabled?: boolean;
  active?: boolean;
  testId?: string;
  Appearances?: ReactElement;
  trimTrigger?: boolean;
  setTrimTrigger?: (off: boolean) => void;
  omitTitle?: boolean;
  omitPlatforms?: boolean;
}

const SelectorTreeHeader = ({
  disabled,
  hasAppearances,
}: {
  originalSelector: string;
  trimming: boolean;
  disabled?: boolean;
  hasAppearances?: boolean;
}) => {
  const { collapsed } = useContext(CollapsibleSectionContext);

  const intl = useIntl();
  const label = intl.formatMessage({ defaultMessage: 'Selector tree', id: 'TPXMyx' });

  return (
    <Pane
      onClick={e => {
        if (disabled) {
          e.stopPropagation();
        }
      }}
      style={disabled ? { cursor: 'default' } : undefined}
      backgroundColor="secondary"
      flex={1}
      paddingTop={8}
      paddingBottom={8}
      paddingLeft={4}
      borderTop="standard"
      borderBottom={collapsed ? undefined : 'standard'}
      borderRadiusBottomLeft={collapsed && !hasAppearances ? 4 : undefined}
      borderRadiusBottomRight={collapsed && !hasAppearances ? 4 : undefined}
      data-component="SelectorTreeHeader"
    >
      <Flex gap={8} justifyContent="flex-start">
        <CollapsibleSection.Icon color={disabled ? 'secondary' : undefined} size={16} />
        <CollapsibleSection.Label
          color={disabled ? 'secondary' : undefined}
          appearance="bodyMedium"
        >
          {label}
        </CollapsibleSection.Label>
      </Flex>
    </Pane>
  );
};

const useSelectorVolume = (selector: PlatformSelectorConfig): { volume: number | null } => {
  const [volume, setVolume] = useState<number | null>(null);
  const appearances = useContext(AppearancesContext);
  const appearancesMetricFn = appearances?.getMetricFn;
  const platforms = selector.platforms;
  const getMetricFn = useCallback(
    (selector: string): Metric | null => {
      if (!selector || !SelectorRule.isValid(selector) || !appearancesMetricFn) {
        return null;
      }
      const event: Event = {
        primary: null,
        deps: makePlatformSelectorDeps({ selector, platforms }),
      };
      return appearancesMetricFn({ event });
    },
    [appearancesMetricFn, platforms],
  );

  const { fetchTotalVolume } = useOptimizeUtil(getMetricFn);
  useEffect(() => {
    if (!selector.selector) {
      setVolume(null);
      return;
    }
    fetchTotalVolume(selector.selector).then(setVolume);
  }, [selector.selector, fetchTotalVolume]);

  return { volume };
};

export const SelectorInputTree: React.FC<SelectorInputTreeProps> = ({
  innerRef,
  selector,
  validationState: _selectorValidation,
  onChange,
  onDelete,
  onFocus,
  onHoverSelector,
  disabled,
  active,
  testId,
  Appearances,
  trimTrigger,
  setTrimTrigger,
  omitTitle,
  omitPlatforms,
}) => {
  const intl = useIntl();
  const inSignalStudio = useContext(InSignalStudioContext);
  const flags = useFlags();
  const webOnly = !flags.hasBit(Flag.FeatNativeMobile);
  const signalStudioEnabled = flags.featureFlags().AppObjectPickerForElements && !inSignalStudio;
  const selectorTreeEnabled = inSignalStudio;
  const debugEnabled = flags.featureFlags().AppSelectorDebug;
  const { experimentOptedOut } = useExperimentConfig();
  const selectorTrimOptedOut = experimentOptedOut[ExperimentFeature.FEATURE_SELECTOR_TRIM];
  const showTree = useMemo(
    () => selectorTreeEnabled && !containsNonDescendantCombinator(selector.selector),
    [selector.selector, selectorTreeEnabled],
  );

  const { volume } = useSelectorVolume(selector);

  // separate state for text selector so we can keep it nice and stable
  const [textSelector, setTextSelector] = useState<string>(selector.selector);
  useEffect(() => {
    setTextSelector(selector.selector);
  }, [selector.selector]);
  const commitTextSelector = useCallback(() => {
    const formattedSelector = SelectorRule.format(textSelector);
    // even if textSelector can't be formatted (due to invalid) we still want to propagate up so that we can show errors
    // otherwise, on save, the user would be saving something else other than what the text area shows
    const updatedSelector = formattedSelector ? formattedSelector : textSelector;
    if (updatedSelector !== selector.selector) {
      onChange({
        ...selector,
        selector: updatedSelector,
        originalSelector: updatedSelector,
        canonicalizedSelector: null,
      });
    }
  }, [textSelector, selector, onChange]);

  const [abortController, setAbortController] = useState(new AbortController());
  const abortTrim = useCallback(() => {
    abortController.abort();
  }, [abortController]);

  const [signalStudioOpen, setSignalStudioOpen] = useState(false);
  const { SignalStudio, signalStudioProps } = useElementPickerSignalStudio({
    initialSelector: textSelector,
    onConfirm: s => {
      onChange({ ...selector, selector: s });
      setSignalStudioOpen(false);
    },
  });

  // keep validation as local state so that we can clear when the corresponding fields are focused
  const [selectorValidation, setSelectorValidation] = useState(_selectorValidation);
  // but we will still update the validation state when the caller passes new validation in
  useEffect(() => {
    setSelectorValidation(_selectorValidation);
  }, [_selectorValidation]);

  const unsupportedSelector = useMemo(() => {
    const isValidFormattedSelector = SelectorRule.isValid(selector.selector);
    return isValidFormattedSelector && !isSupportedSelectorForSearchAndAnalytics(selector.selector);
  }, [selector.selector]);

  const {
    selectorParts,
    setSelectorParts,
    serializedSelector,
    onHoverSelectorPart,
    onTrimStart,
    onTrimPartial,
    onTrimFinish,
    trimming,
    trimmedSelectorParts,
  } = useSelectorTree(
    selector.originalSelector ?? selector.selector,
    selector.canonicalizedSelector ?? null,
    (s: string) => onChange({ ...selector, selector: s }),
    onHoverSelector,
    showTree,
  );

  // Taken from the Appearances warning banner
  const warningBannerProps = {
    borderRadius: undefined,
    borderRadiusBottomLeft: 4 as 4,
    borderRadiusBottomRight: 4 as 4,
  };

  const [isSelectorTreeCollapsed, setIsSelectorTreeCollapsed] = useState(true);
  const trimState =
    volume === 0 && debugEnabled ? SelectorTrimmingState.FIXING : SelectorTrimmingState.TRIMMING;

  return (
    <Pane data-component="SelectorInputTree">
      <Box display="flex" gap={8} alignItems="stretch" flexDirection="column">
        {!omitTitle && (
          <Box>
            <Flex justifyContent="space-between">
              <Text appearance="bodyMediumBold">
                <FormattedMessage defaultMessage="Selector" id="gxkld2" />
              </Text>
              <Flex gap={4}>
                {!selectorTrimOptedOut &&
                  selectorParts &&
                  selectorParts.length > 1 &&
                  (!debugEnabled || (debugEnabled && (volume == null || volume > 0))) && (
                    <SelectorTrimButton
                      selector={serializedSelector}
                      platforms={selector.platforms}
                      onStart={onTrimStart}
                      onFinish={onTrimFinish}
                      onPartial={onTrimPartial}
                      trimTrigger={trimTrigger}
                      setTrimTrigger={setTrimTrigger}
                      abortController={abortController}
                      setAbortController={setAbortController}
                      trimming={trimming}
                    />
                  )}
                {!selectorTrimOptedOut &&
                  selectorParts &&
                  selectorParts.length > 1 &&
                  volume === 0 &&
                  debugEnabled && (
                    <SelectorFixButton
                      selector={serializedSelector}
                      platforms={selector.platforms}
                      onStart={onTrimStart}
                      onFinish={onTrimFinish}
                      onPartial={onTrimPartial}
                      trimTrigger={trimTrigger}
                      setTrimTrigger={setTrimTrigger}
                      abortController={abortController}
                      setAbortController={setAbortController}
                      trimming={trimming}
                    />
                  )}
                {onDelete && !disabled && (
                  <IconButton
                    iconName="close"
                    title={intl.formatMessage({ defaultMessage: 'Close', id: 'rbrahO' })}
                    variant="minimal"
                    onClick={onDelete}
                  />
                )}
              </Flex>
            </Flex>
          </Box>
        )}
        <Pane
          className={classNames(css.selectorInput, { [css.active]: active })}
          border="standard"
          borderRadius={4}
          maxWidth="100%"
        >
          <Flex justifyContent="space-between">
            <Box className={css.selectorInput} maxWidth="100%">
              <Flex gap={8} alignItems="center" justifyContent="stretch">
                <Pane
                  borderBottom={Appearances ? 'standard' : undefined}
                  borderRadiusTopRight={4}
                  borderRadiusBottomRight={Appearances ? 0 : 4}
                  flex={1}
                  maxWidth="100%"
                >
                  <Box
                    flexGrow={1}
                    display="flex"
                    alignItems="stretch"
                    flexDirection="column"
                    justifyContent="center"
                  >
                    <div style={{ position: 'relative', height: '100%' }}>
                      <TextArea
                        ref={innerRef}
                        spellCheck={false}
                        autoCorrect="off"
                        autoCapitalize="off"
                        onChange={e => setTextSelector(e.target.value)}
                        onFocus={() => {
                          setSelectorValidation(undefined);
                          onFocus?.();
                        }}
                        onBlur={commitTextSelector}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            commitTextSelector();
                          }
                        }}
                        value={textSelector}
                        placeholder={intl.formatMessage({
                          defaultMessage: `e.g. button[data-component='checkout-button']`,
                          id: 'bpoQsa',
                        })}
                        disabled={disabled}
                        invalid={selectorValidation?.invalid !== undefined}
                        testId={testId}
                        aria-labelledby="selector"
                        rows={inSignalStudio ? 3 : 1} // signal studio has a narrower config area so we need more vertical space by default
                        resizable
                        style={{
                          paddingTop: '8px',
                          paddingBottom: '8px',
                          display: 'flex',
                          backgroundColor: 'transparent',
                          borderRadius: '0px',
                          ...(selectorValidation?.invalid !== undefined ? {} : { border: 'none' }),
                        }}
                      />
                      {trimming && isSelectorTreeCollapsed && (
                        <SelectorTrimmingBanner state={trimState} abortTrim={abortTrim} />
                      )}
                    </div>
                    {showTree && (
                      <CollapsibleSection
                        initiallyCollapsed={isSelectorTreeCollapsed}
                        onCollapsedChange={setIsSelectorTreeCollapsed}
                      >
                        <CollapsibleSection.Header>
                          <SelectorTreeHeader
                            originalSelector={selector.originalSelector}
                            trimming={trimming}
                            disabled={!selector.originalSelector}
                            hasAppearances={!!Appearances}
                          />
                        </CollapsibleSection.Header>
                        <CollapsibleSection.Panel>
                          <Box padding={8}>
                            <SelectorTree
                              selectorParts={selectorParts}
                              setSelectorParts={setSelectorParts}
                              trimming={trimming}
                              trimmedSelectorParts={trimmedSelectorParts}
                              onHoverSelectorPart={onHoverSelectorPart}
                              abortTrim={abortTrim}
                              trimState={trimState}
                            />
                          </Box>
                        </CollapsibleSection.Panel>
                      </CollapsibleSection>
                    )}
                  </Box>
                </Pane>
                {signalStudioEnabled && (
                  <Button
                    onClick={() => setSignalStudioOpen(true)}
                    variant="fill-secondary"
                    testId="object-picker-find-selector-button"
                  >
                    Find selector
                  </Button>
                )}
              </Flex>
            </Box>
          </Flex>
          {selectorValidation?.invalid?.message && (
            <Box testId="selector-validation-error" padding={[0, 8, 8, 8]}>
              <InputDescription invalid description={selectorValidation?.invalid?.message} />
            </Box>
          )}
          {unsupportedSelector && (
            <WarningBanner {...warningBannerProps}>
              <Text appearance="bodyMedium">
                <FormattedMessage
                  defaultMessage="This selector is unsupported for search and analytics. <a>Learn more</a>"
                  id="uN5jTl"
                  values={{
                    a: (chunks: React.ReactNode) => (
                      <InlineLink
                        href="https://help.fullstory.com/hc/en-us/articles/360053309534-How-can-I-use-CSS-selectors-in-Fullstory-Search"
                        target="_blank"
                      >
                        {chunks}
                      </InlineLink>
                    ),
                  }}
                />
              </Text>
            </WarningBanner>
          )}
          {Appearances}
        </Pane>
        {!omitPlatforms && (
          <Box testId="multi-platform-selector">
            <MultiPlatformSelection
              disabled={disabled || webOnly}
              platforms={new Set(selector.platforms)}
              onChange={updatedPlatforms => {
                onChange({ ...selector, platforms: [...updatedPlatforms] });
              }}
              showError={selector.selector.length > 0}
              boldLabel
            />
          </Box>
        )}
        {signalStudioOpen && SignalStudio && (
          <SignalStudio
            {...signalStudioProps}
            header={intl.formatMessage({ defaultMessage: 'Find selector', id: 'EPAaNC' })}
            onCloseSignal={() => {
              setSignalStudioOpen(false);
            }}
          />
        )}
      </Box>
    </Pane>
  );
};
